import React, { useEffect, useState } from 'react';
import { Tooltip, message } from 'antd';

import { companyDocumentLink, getCompanyCredentialDetails } from '../../util/api';
import { withCompanyCredentialDetails } from '../../util/companyCredentialDetails';

// Only these listing types belong to a company and carry company details. Candidate, job and RFP
// listings have none, so there is nothing to ask for.
const COMPANY_LISTING_TYPES = ['customer', 'agency'];

// A company's owners, tax ID number, W-9 and insurance certificate are not part of the listing anyone
// can read. For a signed-in viewer of a company's listing the page asks for them; the server answers
// only the company itself, and the page shows them only when it did. `listing` comes back with them
// laid over its public data.
export const useCompanyCredentialDetails = ({ listing, currentUser }) => {
  const listingId = listing?.id?.uuid;
  const signedIn = !!currentUser?.id?.uuid;
  const isCompanyListing = COMPANY_LISTING_TYPES.includes(listing?.attributes?.publicData?.listingType);
  const [loaded, setLoaded] = useState({ listingId: null, details: null });

  useEffect(() => {
    if (!listingId || !signedIn || !isCompanyListing) return undefined;

    let cancelled = false;
    getCompanyCredentialDetails(listingId)
      .then(details => {
        if (!cancelled) setLoaded({ listingId, details: details || null });
      })
      .catch(() => {
        if (!cancelled) setLoaded({ listingId, details: null });
      });
    return () => {
      cancelled = true;
    };
  }, [listingId, signedIn, isCompanyListing]);

  // Details loaded for a previous listing never show on this one.
  const details = signedIn && isCompanyListing && loaded.listingId === listingId ? loaded.details : null;

  return {
    listing: withCompanyCredentialDetails(listing, details),
    credentialsLoaded: !!details,
  };
};

// The W-9 and insurance certificate are for the company and for a visitor who verified a sharing link.
export const canSeeCompanyDocuments = ({ credentialsLoaded, shareOtpVerified }) =>
  !!(credentialsLoaded || shareOtpVerified);

// Opens a W-9 or insurance certificate through a short-lived link made for this viewer. The tab is
// opened on the click itself, before the link is asked for, so the browser does not block it.
// `hasFile` is known only to the company; a sharing-link viewer is offered the button either way.
// A sharing-link viewer's request carries their share visit ({ token, session }), sent in headers.
export const CompanyDocumentFile = ({ listingId, document, shareVisit, hasFile }) => {
  if (hasFile === false) return <h6>-</h6>;

  const open = async () => {
    const tab = window.open('', '_blank');
    try {
      const body = { listingId, document };
      const { url } = shareVisit
        ? await companyDocumentLink(body, { shareVisit })
        : await companyDocumentLink(body);
      if (tab) {
        tab.opener = null;
        tab.location.href = url;
      } else {
        window.location.assign(url);
      }
    } catch (error) {
      if (tab) tab.close();
      message.info(error?.status === 404 ? 'No file on record' : 'This file is not available');
    }
  };

  return (
    <h6>
      <Tooltip title="Click to view file">
        <a role="button" tabIndex={0} onClick={open}>
          File
        </a>
      </Tooltip>
    </h6>
  );
};
